import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import {
    GetFundingRequirementsCreditFacilityResponse,
    GetStateResponse
} from '@iwoca/lapi-client/edge';
import {
    TDeserialisedPeople,
    TDeserialisedPerson,
    TOnfidoSerialisedStatuses,
    TSerialisedPeople
} from './OnfidoVerificationModal.types';

export function buildOnfidoMessagesSent(
    previousState: string[] | null,
    currentUID: string
) {
    if (!previousState) {
        return [currentUID];
    }

    return _.union(previousState, [currentUID]);
}

export function deserialiseOnfidoResponse(
    customer_state: GetStateResponse['data'],
    funding_requirement: GetFundingRequirementsCreditFacilityResponse['data']
): { peopleToVerify: TDeserialisedPeople } | undefined {
    const onfidoResolutionMethod = _.find(funding_requirement, {
        recommended_resolution_method: 'onfido_check'
    });

    if (!onfidoResolutionMethod) return undefined;

    const onfidoChecks = onfidoResolutionMethod?.resolution_methods;

    const onfidoStatuses = !!onfidoChecks
        ? _.find(onfidoChecks, { type: 'onfido_check' })?.items
        : undefined;

    const onlyApplicantsGuarantors = _.chain(customer_state.application!.people)
        .filter((person) => {
            const roles = person?.roles ?? [];
            return (
                roles.indexOf('applicant') > -1 ||
                roles.indexOf('guarantor') > -1
            );
        })
        .value();
    return {
        peopleToVerify: deserialisePeople(
            onlyApplicantsGuarantors,
            onfidoStatuses
        )
    };
}

export function serialisePersonState(
    people: TDeserialisedPeople,
    selectedPerson: TDeserialisedPerson
) {
    return _.map(people, (person) => {
        if (person.uid === selectedPerson.uid) {
            if (person.phones) {
                person.phones.push({
                    number: selectedPerson.newPhoneNumber!,
                    uid: uuidv4(),
                    type: 'secondary'
                });
            } else {
                person.phones = [
                    {
                        number: selectedPerson.newPhoneNumber,
                        uid: uuidv4(),
                        type: 'primary'
                    }
                ];
            }
        }
        return person;
    });
}

function deserialisePeople(
    people: TSerialisedPeople,
    onfidoStatuses: TOnfidoSerialisedStatuses
): TDeserialisedPeople {
    return _.map(people, (person) => {
        return {
            uid: person.uid,
            title: person?.title!,
            firstName: person?.first_name!,
            lastName: person?.last_name!,
            roles: person?.roles,
            phones: deserialisePhones(person.phones),
            onfidoStatus: getOnfidoStatus(onfidoStatuses, person.uid),
            addPhoneNumberPending: false
        };
    });
}

function getOnfidoStatus(
    onfidoStatusList: TOnfidoSerialisedStatuses,
    uid: string
) {
    const onfidoStatus = _.find(onfidoStatusList, { name: uid });
    if (!!onfidoStatus) {
        if (onfidoStatus.status === 'completed') {
            return 'Verified';
        }
        if (onfidoStatus.status === 'pending') {
            return 'Verifying your check';
        }
        if (onfidoStatus.status === 'error') {
            return 'Something went wrong';
        }
        return 'Not started yet';
    } else {
        return 'Something went wrong';
    }
}

function deserialisePhones(
    phones: NonNullable<TSerialisedPeople>[number]['phones']
) {
    const deserialisedPhones: {
        uid?: string;
        label?: string;
        value?: string | null;
        type?: 'primary' | 'secondary' | 'ADD_NEW';
    }[] = _.map(phones, (phone) => {
        return {
            uid: phone.uid,
            label: phone.number,
            value: phone.number
        };
    });

    deserialisedPhones.push({
        type: 'ADD_NEW',
        label: 'Add new',
        value: null
    });

    return deserialisedPhones;
}

export function toggleAddPhoneNumber(
    people: TDeserialisedPeople,
    personUID: string
) {
    const peopleToVerify = _.cloneDeep(people);

    _.map(peopleToVerify, (person) => {
        if (person!.uid === personUID) {
            _.extend(person, { addPhoneToggle: !person!.addPhoneToggle });
        }
    });

    return peopleToVerify;
}

export function updatePeopleState(
    people: TDeserialisedPeople,
    personUID: string,
    overrideObj: {
        newPhoneNumber?: string;
        error?: boolean;
        SMSPending?: boolean;
        status?: string;
        addPhoneNumberPending?: boolean;
    }
) {
    const peopleToVerify = _.cloneDeep(people);

    _.map(peopleToVerify, (person) => {
        if (person.uid === personUID) {
            _.extend(person, overrideObj);
        }
    });

    return peopleToVerify;
}

export function isValid(phoneNumber: string) {
    if (getCountry() === 'DE') {
        return /^(0|(\+49)|0049)[0-9]{8,15}$/.test(phoneNumber);
    } else {
        return /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/.test(
            phoneNumber
        );
    }
}
